import type { DeviceViewSettingsTable } from '@ipfabric/utils';
import type { SelectedTables } from 'components/DeviceView/deviceViewTypes';
import { type Draft, produce } from 'immer';
import { type FC, Fragment, memo, useMemo } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { dragTypes } from 'components/DeviceView/deviceViewConstants';
import {
  type MenuTreeNode,
  type PageTitleTranslations,
  type UrlListItem,
  isPageItem,
} from 'components/Sidebar/menuTree';
import type { RoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import { useDrag } from 'react-dnd';

interface TreeRowProps {
  data: MenuTreeNode[];
  onAddTable: (data: SelectedTables) => void;
  routesWithApiEndpoint: RoutesWithApiEndpoint;
  selectedTables: SelectedTables;
  tree: MenuTreeNode[];
}

interface DraggableTreeItemProps {
  msg: PageTitleTranslations;
  onAddTable: (data: SelectedTables) => void;
  path: string[];
  selectedTables: SelectedTables;
  webEndpoint: string;
}

const DraggableTreeItem: FC<DraggableTreeItemProps> = (props) => {
  const label = (props.msg.boxTitle || props.msg.pageTitle) ?? '';

  const isSelectedTable = useStableCallback(
    (table: DeviceViewSettingsTable): boolean =>
      table.webEndpoint === props.webEndpoint,
  );

  const tableSelected = useMemo(
    () => props.selectedTables.some(isSelectedTable),
    [props.selectedTables],
  );

  const path = useMemo(() => {
    if (props.path) {
      return `(${props.path.join(' - ')})`;
    }
    return '';
  }, [props.path]);

  const handleAddTable = useStableCallback(() => {
    const foundTable = props.selectedTables.some(isSelectedTable);
    if (!foundTable) {
      props.onAddTable(
        produce(props.selectedTables, (draft: Draft<SelectedTables>) => {
          draft.push({
            label,
            path,
            webEndpoint: props.webEndpoint,
          });
        }),
      );
    }
  });

  const [, drag] = useDrag({
    item: {
      label,
      path,
      type: dragTypes.TREE,
      webEndpoint: props.webEndpoint,
    },
    canDrag() {
      return !tableSelected;
    },
  });

  return (
    <div
      className={classNames(
        'device-view-setup-tables__search-results-item flex flex-row flex-nowrap items-center',
        tableSelected ? 'text-disabled' : 'cursor-move',
        'pl-lgu-3 pt-lgu-2 pb-lgu-2 pr-lgu-3',
      )}
      ref={drag}
      onDoubleClick={handleAddTable}
    >
      <Button
        variant='tertiary'
        icon='fa-circle-plus'
        disabled={tableSelected}
        className='mr-lgu-3'
        size='xxs'
        onClick={handleAddTable}
        type='button'
      />
      <div className='flex flex-row flex-wrap'>
        <span className='mr-lgu-3 whitespace-nowrap font-bold'>{label}</span>
        <span className='mr-lgu-3 whitespace-nowrap'>{path}</span>
      </div>
    </div>
  );
};

const TreeRow: FC<TreeRowProps> = (props) => {
  const rows = props.data.map((menuTreeNode: MenuTreeNode, index) => {
    return (
      <Fragment key={`${menuTreeNode.label}-${index.toString()}`}>
        {menuTreeNode.children && menuTreeNode.children.length > 0 && (
          <TreeRow
            data={menuTreeNode.children}
            onAddTable={props.onAddTable}
            routesWithApiEndpoint={props.routesWithApiEndpoint}
            selectedTables={props.selectedTables}
            tree={props.tree}
          />
        )}
        {Array.isArray(menuTreeNode.urlList) &&
          menuTreeNode.urlList.length > 0 &&
          menuTreeNode.urlList.map((pageItem: UrlListItem) => {
            if (!isPageItem(pageItem)) {
              return null;
            }
            if (!props.routesWithApiEndpoint[pageItem.url]) {
              return null;
            }
            return (
              <DraggableTreeItem
                key={pageItem.url}
                msg={pageItem.msg}
                onAddTable={props.onAddTable}
                path={pageItem.path ?? []}
                selectedTables={props.selectedTables}
                webEndpoint={pageItem.url}
              />
            );
          })}
      </Fragment>
    );
  });

  return <>{rows}</>;
};

interface TreeProps {
  data: MenuTreeNode[];
  onAddTable: (data: SelectedTables) => void;
  routesWithApiEndpoint: RoutesWithApiEndpoint;
  selectedTables: SelectedTables;
}

const BareDeviceViewSetupTablesSearchResults: FC<TreeProps> = (props) => {
  return (
    <TreeRow
      data={props.data}
      onAddTable={props.onAddTable}
      routesWithApiEndpoint={props.routesWithApiEndpoint}
      selectedTables={props.selectedTables}
      tree={props.data}
    />
  );
};

export const DeviceViewSetupTablesSearchResults = memo(
  BareDeviceViewSetupTablesSearchResults,
);
